import { useEffect, useState, useCallback, useMemo } from 'react';
import type { ServerTask, ViewId } from '../state';
import { api, type TaskCategory, type TopCat, type TopRow } from '../lib/api';
import { WheelView } from './Wheel';
import { isNew, markSeen } from '../lib/newFeatures';

type Props = {
    onNavigate:      (id: ViewId) => void;
    onExecTask:      (t: ServerTask) => void;
    onCreateTask:    () => void;
    /** Opens the creator's re-review queue (пересмотры по моим заданиям). */
    onReviewAppeals?: () => void;
    canCreateTask?:  boolean;
    /** Колесо: open the top-up flow / refresh the balance after a BSP prize. */
    onTopUp?:          () => void;
    onBalanceChanged?: () => void;
    /** Root sees where to fill an empty wheel. */
    isRoot?:           boolean;
    tasksVersion?:   number;
    referralCode:    string;
    referralCount:   number;
    botUsername:     string;
};

type TabId = 'tasks' | 'wheel' | 'refs' | 'top';
/** List sections from the canvas: three categories plus the hold view. */
type ListTab = TaskCategory | 'hold';

const LIST_TABS: ReadonlyArray<{ id: ListTab; label: string }> = [
    { id: 'fast',    label: 'Быстрые' },
    { id: 'regular', label: 'Обычные' },
    { id: 'special', label: 'Особые' },
    { id: 'hold',    label: 'Холд' },
];

const LIST_HINTS: Record<ListTab, string> = {
    fast:    'Проверяются автоматически — ботом или проверочным словом.',
    regular: 'Проверяются модератором по скриншоту.',
    special: 'Задания от администрации: игры, пополнения, бонусы.',
    hold:    'Сданные и принятые задания, по которым ещё идёт холд.',
};

const TOP_LABELS: Record<TopCat, string> = {
    buy:       'Топ по покупкам BSP',
    refs:      'Топ по рефералам',
    refEarn:   'Топ по заработку на рефералах',
    tasksDone: 'Топ по выполненным заданиям',
};

// ─── Filters (Авто-фильтр) ───────────────────────────────────────────────────

type Filters = { hideUnavailable: boolean; hideDone: boolean };
const FILTERS_KEY = 'bog.taskFilters';
const DEFAULT_FILTERS: Filters = { hideUnavailable: true, hideDone: true };

function loadFilters(): Filters {
    try {
        const raw = localStorage.getItem(FILTERS_KEY);
        if (!raw) return DEFAULT_FILTERS;
        const p = JSON.parse(raw) as Partial<Filters>;
        return { ...DEFAULT_FILTERS, ...p };
    } catch { return DEFAULT_FILTERS; }
}
function saveFilters(f: Filters) {
    try { localStorage.setItem(FILTERS_KEY, JSON.stringify(f)); } catch { /* private mode */ }
}

// ─── Task state ───────────────────────────────────────────────────────────────

type CardState =
    | 'unavailable' | 'done' | 'holding' | 'review' | 'appeal_review' | 'rejected'
    | 'rejected_final' | 'in_progress' | 'full' | 'special_start' | 'special_claim'
    | 'special_progress' | 'special_info' | 'idle';

export function cardState(t: ServerTask): CardState {
    if (!t.available) return 'unavailable';
    if (t.special) {
        const p = t.special.progress;
        if (t.special.type === 'deposit_ladder') return 'special_info';
        if (p.claimed) return 'done';
        if (!p.started) return 'special_start';
        return p.done ? 'special_claim' : 'special_progress';
    }
    if (t.myStatus === 'holding') return 'holding';
    if (t.myStatus === 'completed' || t.mySubmission?.status === 'accepted') return 'done';
    if (t.mySubmission?.status === 'pending') return 'review';
    if (t.mySubmission) {
        if (t.mySubmission.appeals.some((a) => a.status === 'pending')) return 'appeal_review';
        return t.mySubmission.canAppeal ? 'rejected' : 'rejected_final';
    }
    if (t.myStatus === 'in_progress') return 'in_progress';
    if (t.slotsLeft <= 0) return 'full';
    return 'idle';
}

/** "Выполненные" for the hide filter: nothing left for the user to do. */
export function isDone(t: ServerTask): boolean {
    const s = cardState(t);
    return s === 'done' || s === 'holding' || s === 'rejected_final';
}

function actionLabel(t: ServerTask, s: CardState): string {
    switch (s) {
        case 'unavailable':      return '⭐ Только для Telegram Premium';
        case 'done':             return t.special?.type === 'daily_bonus' ? '✓ Получено сегодня' : '✓ Выполнено';
        case 'holding':          return t.holdUntil ? `⏳ Холд до ${fmtShortDate(t.holdUntil)}` : '⏳ Холд идёт';
        case 'review':           return '⏳ На проверке';
        case 'appeal_review':    return '⏳ Пересмотр рассматривается';
        case 'rejected':         return 'Отклонено — запросить пересмотр';
        case 'rejected_final':   return '✗ Отклонено';
        case 'in_progress':      return '→ Продолжить';
        case 'full':             return '✗ Места закончились';
        case 'special_start':    return 'Начать';
        case 'special_claim':    return t.special?.type === 'daily_bonus' ? 'Забрать бонус' : 'Забрать награду';
        case 'special_progress': return 'Подробнее';
        case 'special_info':     return 'Подробнее';
        default:                 return 'Выполнить';
    }
}

function clickable(s: CardState): boolean {
    return !['unavailable', 'done', 'holding', 'review', 'appeal_review', 'rejected_final', 'full'].includes(s);
}

function stateColor(s: CardState): string {
    switch (s) {
        case 'done':          return 'var(--green)';
        case 'holding':
        case 'review':
        case 'appeal_review': return 'var(--accent)';
        case 'rejected':
        case 'rejected_final': return 'var(--red)';
        case 'in_progress':
        case 'special_claim': return 'var(--blue)';
        default:              return 'var(--border)';
    }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function BonusesView({
    onNavigate, onExecTask, onCreateTask, onReviewAppeals, onTopUp, onBalanceChanged, isRoot = false,
    canCreateTask = false, tasksVersion = 0,
    referralCode, referralCount, botUsername,
}: Props) {
    const [tab, setTab] = useState<TabId>('tasks');
    const [wheelNew, setWheelNew] = useState(() => isNew('wheel'));
    // Opening the wheel once retires every "NEW" marker for it.
    useEffect(() => {
        if (tab === 'wheel' && wheelNew) { markSeen('wheel'); setWheelNew(false); }
    }, [tab, wheelNew]);

    // Tasks
    const [tasks,    setTasks]    = useState<ServerTask[] | null>(null);
    const [tasksErr, setTasksErr] = useState<string | null>(null);
    const [stats,    setStats]    = useState<{ total: number; accepted: number; pending: number; earned: number } | null>(null);
    const [incoming, setIncoming] = useState(0);
    const [listTab,  setListTab]  = useState<ListTab>('fast');
    const [filters,  setFilters]  = useState<Filters>(loadFilters);

    const toggleFilter = (k: keyof Filters) => setFilters((f) => {
        const next = { ...f, [k]: !f[k] };
        saveFilters(next);
        return next;
    });

    const loadTasks = useCallback(async () => {
        setTasksErr(null);
        try {
            const r = await api.listTasks();
            setTasks(r.items);
        } catch (e) { setTasksErr(String(e)); setTasks([]); }
        try { const s = await api.myTaskStats(); setStats(s); } catch { /* non-critical */ }
        try { const a = await api.incomingAppeals(); setIncoming(a.items.length); } catch { setIncoming(0); }
    }, []);

    useEffect(() => { if (tab === 'tasks') void loadTasks(); }, [tab, tasksVersion, loadTasks]);

    const counts = useMemo(() => {
        const c: Record<ListTab, number> = { fast: 0, regular: 0, special: 0, hold: 0 };
        for (const t of tasks ?? []) {
            if (t.myStatus === 'holding') c.hold++;
            if (filters.hideUnavailable && !t.available) continue;
            if (filters.hideDone && isDone(t)) continue;
            c[t.category]++;
        }
        return c;
    }, [tasks, filters]);

    const visible = useMemo(() => {
        if (!tasks) return null;
        if (listTab === 'hold') return tasks.filter((t) => t.myStatus === 'holding');
        return tasks.filter((t) => t.category === listTab
            && !(filters.hideUnavailable && !t.available)
            && !(filters.hideDone && isDone(t)));
    }, [tasks, listTab, filters]);

    // Refs
    const [serverRef, setServerRef] = useState<{ code: string; earned: number; totalRefs: number } | null>(null);
    useEffect(() => {
        if (tab !== 'refs') return;
        api.getReferrals().then((r) => setServerRef({ code: r.code, earned: r.earned, totalRefs: r.totalRefs })).catch(() => {});
    }, [tab]);

    // Top
    const [topCat,  setTopCat]  = useState<TopCat>('buy');
    const [topRows, setTopRows] = useState<TopRow[] | null>(null);
    const [topErr,  setTopErr]  = useState<string | null>(null);
    useEffect(() => {
        if (tab !== 'top') return;
        let cancelled = false;
        setTopRows(null); setTopErr(null);
        api.listTops(topCat)
            .then((r) => { if (!cancelled) setTopRows(r.items); })
            .catch((e) => { if (!cancelled) setTopErr(String(e)); });
        return () => { cancelled = true; };
    }, [tab, topCat]);

    const refCode   = serverRef?.code ?? referralCode;
    const refsCount = serverRef?.totalRefs ?? referralCount;

    return (
        <div className="view">
            {/* Tabs */}
            <div className="tabs" role="tablist">
                {(['tasks', 'wheel', 'refs', 'top'] as TabId[]).map((t) => (
                    <button key={t} type="button"
                        className={`tab-btn ${tab === t ? 'active' : ''}`}
                        onClick={() => setTab(t)}>
                        {t === 'tasks' ? 'Задания' : t === 'wheel' ? 'Колесо' : t === 'refs' ? 'Рефералы' : 'Топ-10'}
                        {t === 'wheel' && wheelNew && <span className="tab-new" data-testid="tab-new-wheel">NEW</span>}
                    </button>
                ))}
            </div>

            {/* ═══════════════════════════════════ TASKS ═════════════════════════════ */}
            {tab === 'tasks' && (
                <>
                    {wheelNew && (
                        <button type="button" className="new-feature-banner" data-testid="wheel-new-banner"
                            onClick={() => setTab('wheel')}>
                            <span className="new-feature-icon">🎡</span>
                            <span className="new-feature-txt">
                                <b>Новое: колесо фортуны</b>
                                <span>Бесплатная крутка каждый день — BSP и бонусы к депозиту</span>
                            </span>
                            <span className="new-feature-go">КРУТИТЬ</span>
                        </button>
                    )}
                    {/* Personal stats bar */}
                    {stats && (stats.accepted > 0 || stats.pending > 0) && (
                        <div className="task-stats">
                            <div>
                                <div className="task-stat-num" style={{ color: 'var(--green)' }}>{stats.accepted}</div>
                                <div className="task-stat-label">Принято</div>
                            </div>
                            <div>
                                <div className="task-stat-num" style={{ color: 'var(--accent)' }}>{stats.pending}</div>
                                <div className="task-stat-label">На проверке</div>
                            </div>
                            <div>
                                <div className="task-stat-num" style={{ color: 'var(--accent)' }}>
                                    +{stats.earned.toLocaleString('ru-RU')}
                                </div>
                                <div className="task-stat-label">BSP заработано</div>
                            </div>
                        </div>
                    )}

                    {/* Re-reviews waiting for me as a creator */}
                    {incoming > 0 && onReviewAppeals && (
                        <button type="button" className="appeals-banner" onClick={onReviewAppeals}>
                            <span>⚖️ Пересмотры по вашим заданиям</span>
                            <b>{incoming}</b>
                        </button>
                    )}

                    {/* Sections */}
                    <div className="task-cats" role="tablist" aria-label="Разделы заданий">
                        {LIST_TABS.map((c) => (
                            <button key={c.id} type="button" role="tab"
                                aria-selected={listTab === c.id}
                                className={`task-cat ${listTab === c.id ? 'active' : ''}`}
                                onClick={() => setListTab(c.id)}>
                                {c.label}
                                {counts[c.id] > 0 && <span className="task-cat-count">{counts[c.id]}</span>}
                            </button>
                        ))}
                    </div>

                    {/* Авто-фильтр */}
                    {listTab !== 'hold' && (
                        <div className="task-filters" aria-label="Авто-фильтр">
                            <button type="button" aria-pressed={filters.hideUnavailable}
                                className={`task-filter ${filters.hideUnavailable ? 'on' : ''}`}
                                onClick={() => toggleFilter('hideUnavailable')}>
                                {filters.hideUnavailable ? '✓ ' : ''}Скрыть недоступные
                            </button>
                            <button type="button" aria-pressed={filters.hideDone}
                                className={`task-filter ${filters.hideDone ? 'on' : ''}`}
                                onClick={() => toggleFilter('hideDone')}>
                                {filters.hideDone ? '✓ ' : ''}Скрыть выполненные
                            </button>
                        </div>
                    )}
                    <p className="task-cat-hint">{LIST_HINTS[listTab]}</p>

                    {/* Error / empty / loading */}
                    {tasksErr && (
                        <div style={{ textAlign: 'center', padding: 20 }}>
                            <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>Не удалось загрузить задания</div>
                            <button type="button" className="action-btn btn-outline-gold"
                                style={{ fontSize: 11, padding: '4px 14px' }}
                                onClick={() => void loadTasks()}>Повторить</button>
                        </div>
                    )}
                    {visible === null && !tasksErr && (
                        <div className="task-empty">Загружаем…</div>
                    )}
                    {visible?.length === 0 && !tasksErr && (
                        <div className="task-empty">
                            {listTab === 'hold' ? 'Заданий в холде нет.' : 'В этом разделе пока нет доступных заданий.'}
                        </div>
                    )}

                    <div className="tasks-list">
                        {visible?.map((t) => <TaskCard key={t.id} task={t} onOpen={onExecTask} />)}
                    </div>

                    {canCreateTask && (
                        <button type="button" className="action-btn btn-gold"
                            style={{ padding: 12, marginTop: 10 }}
                            onClick={onCreateTask}>
                            + СОЗДАТЬ ЗАДАНИЕ
                        </button>
                    )}
                </>
            )}

            {/* ═══════════════════════════════════ WHEEL ═════════════════════════════ */}
            {tab === 'wheel' && (
                <WheelView onTopUp={onTopUp} onInvite={() => setTab('refs')} onBalanceChanged={onBalanceChanged} isRoot={isRoot} />
            )}

            {/* ═══════════════════════════════════ REFS ══════════════════════════════ */}
            {tab === 'refs' && (
                <>
                    <div className="info-card text-center" style={{ borderColor: 'var(--accent)' }}>
                        <p className="text-muted f-12">Ваша реферальная ссылка</p>
                        <p style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700, wordBreak: 'break-all', margin: '6px 0' }}>
                            t.me/{botUsername}?startapp={refCode}
                        </p>
                        <button type="button" className="action-btn btn-outline-gold"
                            onClick={() =>
                                navigator.clipboard?.writeText(`https://t.me/${botUsername}?startapp=${refCode}`).catch(() => {})
                            }>
                            КОПИРОВАТЬ
                        </button>
                    </div>
                    <div className="info-card" style={{ borderColor: 'var(--accent)', background: 'rgba(255,215,0,0.05)' }}>
                        <p className="popup-text text-center">
                            🎁 Каждый приглашённый реферал = <b>1 бесплатный билет</b> в BIG BOG GIVEAWAY.
                        </p>
                        <button type="button" className="action-btn btn-gold" onClick={() => onNavigate('bigGiveaway')}>
                            ОТКРЫТЬ BIG BOG GIVEAWAY
                        </button>
                    </div>
                    <div className="info-card">
                        <p className="popup-text">💸 <b>1%</b> с каждой игры реферала</p>
                        <p className="popup-text" style={{ marginTop: 4 }}>🛒 <b>5%</b> с каждой покупки реферала</p>
                        <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                            <span className="text-muted f-12">Рефералов всего:</span>
                            <span style={{ fontWeight: 900, color: 'var(--accent)' }}>{refsCount}</span>
                        </div>
                        {serverRef && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                                <span className="text-muted f-12">Заработано на рефах:</span>
                                <span style={{ fontWeight: 900, color: 'var(--accent)' }}>{serverRef.earned} BSP</span>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ═══════════════════════════════════ TOP ═══════════════════════════════ */}
            {tab === 'top' && (
                <>
                    <div className="info-card" style={{ borderColor: 'var(--accent)', padding: 10 }}>
                        <p className="popup-text" style={{ color: 'var(--accent)', fontWeight: 700 }}>
                            🚀 Скоро: бонусы за призовые места.
                        </p>
                        <p className="popup-text text-muted f-12" style={{ marginTop: 4 }}>
                            Победители ТОПов будут получать награды каждую неделю.
                        </p>
                    </div>
                    <div className="input-group">
                        <select value={topCat} onChange={(e) => setTopCat(e.target.value as TopCat)}>
                            {(Object.keys(TOP_LABELS) as TopCat[]).map((k) => (
                                <option value={k} key={k}>{TOP_LABELS[k]}</option>
                            ))}
                        </select>
                    </div>
                    <div className="info-card" style={{ padding: '4px 10px' }}>
                        {topRows === null && !topErr && (
                            <div style={{ textAlign: 'center', padding: 16, color: '#666', fontSize: 12 }}>Загружаем…</div>
                        )}
                        {topErr && (
                            <div style={{ textAlign: 'center', padding: 16, color: 'var(--red)', fontSize: 12 }}>Не удалось загрузить.</div>
                        )}
                        {topRows?.length === 0 && (
                            <div style={{ textAlign: 'center', padding: 16, color: '#666', fontSize: 12 }}>Пока пусто.</div>
                        )}
                        {topRows?.map((u, i) => (
                            <div key={u.userId} style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '8px 4px',
                                borderBottom: i < topRows.length - 1 ? '1px solid var(--border-soft)' : 'none',
                            }}>
                                <span style={{
                                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 10, fontWeight: 900,
                                    background: i === 0 ? '#ffd700' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : 'var(--bg-elev)',
                                    color: i < 3 ? '#000' : '#888',
                                }}>
                                    {i + 1}
                                </span>
                                <span style={{ flex: 1, fontSize: 12, fontWeight: 700 }}>{u.name}</span>
                                <span style={{ fontSize: 11, fontWeight: 900, color: 'var(--accent)' }}>{u.val}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <button type="button" className="back-btn muted" onClick={() => onNavigate('menu')}>
                НАЗАД В МЕНЮ
            </button>
        </div>
    );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function TaskCard({ task: t, onOpen }: { task: ServerTask; onOpen: (t: ServerTask) => void }) {
    const s = cardState(t);
    const canOpen = clickable(s);
    const color = stateColor(s);
    const progress = t.special?.progress;
    const showSlots = !t.special;
    const filled = t.slotsTotal - t.slotsLeft;
    const slotPct = t.slotsTotal > 0 ? Math.min(filled / t.slotsTotal, 1) : 0;
    const rejectNote = t.mySubmission?.status === 'rejected'
        ? (t.mySubmission.appeals[t.mySubmission.appeals.length - 1]?.decisionComment ?? t.mySubmission.moderatorComment)
        : null;

    return (
        <div className="task-card task-card-v4"
            data-task-id={t.id}
            data-state={s}
            style={{ borderColor: color, opacity: s === 'done' || s === 'unavailable' ? 0.65 : 1, cursor: canOpen ? 'pointer' : 'default' }}
            onClick={() => { if (canOpen) onOpen(t); }}>
            <div className="task-card-head">
                <div className="task-badges">
                    {t.checkType === 'bot'        && <span className="task-badge b-blue">🤖 БОТ</span>}
                    {t.checkType === 'auto'       && <span className="task-badge b-green">👁 ПРОСМОТР</span>}
                    {t.checkType === 'word'       && <span className="task-badge b-green">🔑 СЛОВО</span>}
                    {t.checkType === 'screenshot' && <span className="task-badge b-orange">📸 СКРИН</span>}
                    {t.checkType === 'special'    && <span className="task-badge b-gold">✨ ОСОБОЕ</span>}
                    {t.isPremiumOnly              && <span className="task-badge b-violet">⭐ PREMIUM</span>}
                </div>
                <div className="task-reward-box">
                    {t.special?.type === 'deposit_ladder' ? (
                        <div className="task-reward-num">+{String(t.special.params['stepPct'] ?? 5)}%</div>
                    ) : (
                        <div className="task-reward-num">+{t.reward}<span>BSP</span></div>
                    )}
                    {t.holdHours > 0 && <div className="task-reward-sub">⏳ холд {t.holdHours}ч</div>}
                </div>
            </div>

            <div className="task-card-title">{t.title}</div>
            {t.description && (
                <div className="task-card-desc">
                    {t.description.length > 90 ? t.description.slice(0, 90) + '…' : t.description}
                </div>
            )}

            {rejectNote && (
                <div className="task-card-note">Причина: {rejectNote}</div>
            )}

            {progress && t.special?.type !== 'deposit_ladder' && progress.target > 0 && t.special?.type !== 'daily_bonus' && (
                <Progress label={progress.label} pct={progress.started ? progress.current / progress.target : 0} goal />
            )}
            {progress && (t.special?.type === 'deposit_ladder' || t.special?.type === 'daily_bonus') && (
                <div className="task-card-desc" style={{ color: 'var(--accent)' }}>{progress.label}</div>
            )}
            {showSlots && (
                <Progress
                    label={`Выполнено: ${filled} из ${t.slotsTotal}`}
                    right={t.slotsLeft <= 0 ? 'мест нет' : `${t.slotsLeft} мест`}
                    pct={slotPct}
                    warn={t.slotsLeft <= 3}
                />
            )}

            <button type="button" className="task-action"
                disabled={!canOpen}
                style={{ borderColor: color === 'var(--border)' ? 'var(--accent)' : color, color: color === 'var(--border)' ? 'var(--accent)' : color }}
                onClick={(e) => { e.stopPropagation(); if (canOpen) onOpen(t); }}>
                {actionLabel(t, s)}
            </button>
        </div>
    );
}

/** `goal` — progress towards finishing (fills green); otherwise slot fill,
 *  which turns orange/red as the task runs out of places. */
function Progress({ label, right, pct, warn, goal }: { label: string; right?: string; pct: number; warn?: boolean; goal?: boolean }) {
    const p = Math.max(0, Math.min(1, Number.isFinite(pct) ? pct : 0));
    return (
        <div className="task-progress">
            <div className="task-progress-row">
                <span>{label}</span>
                {right && <span style={{ color: warn ? '#ff8585' : undefined }}>{right}</span>}
            </div>
            <div className="task-progress-bar">
                <div style={{
                    width: `${Math.round(p * 100)}%`,
                    background: goal ? 'var(--green)' : p >= 0.9 ? 'var(--red)' : p >= 0.6 ? 'var(--orange)' : 'var(--accent)',
                }} />
            </div>
        </div>
    );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtShortDate(iso: string) {
    try {
        return new Date(iso).toLocaleString('ru-RU', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
}
